import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { useAppDispatch, useAppSelector } from "../hooks/storeHooks";
import { fetchInspectionData, updateInspectionSectionGrades } from "../store/thunks/inspectionThunks";
import { selectInspection, selectInspectionIsDisabled } from "../store/selectors/inspectionSelectors";
import { getGradingSections, getInspectionGrades } from "../services/gradingService";
import { getCrewWelfareGrade } from "../services/crewWelfareGradeService";
import { InspectionGradeInfo } from "../components/Inspection/InspectionGradeInfo";
import { GradingSummary } from "../components/Grading/GradingSummary";
import { GradingSectionTable, GradingSectionRow } from "../components/Grading/GradingSectionTable";
import { calculateSectionGrades, sectionGradesDto } from "../utils/GradeUtils";
import { GradingSection } from "../types/inspection";

/** The VesselGrade.Name of the automated Crew Welfare sub-grade (A2-9093). */
const CREW_WELFARE_SECTION_NAME = "Crew Welfare";

/** The recorded-grade fields the page needs from GET /inspections/{id}/grades. */
interface RecordedGrade {
    vesselGradeId: number;
    point: number | null;
}

/**
 * Build the draft from the section template overlaid with recorded grades. The
 * template (grading-sections) drives which rows exist and their order, so
 * ungraded inspections with no recorded grades still render every section
 * blank. Stored points are 0–10; the UI works in 0–100 (null/undefined → blank).
 */
const buildDraft = (sections: GradingSection[], recorded: RecordedGrade[]): GradingSectionRow[] => {
    const pointByVesselGradeId = new Map(recorded.map((grade) => [grade.vesselGradeId, grade.point]));

    return [...sections]
        .sort((a, b) => a.order - b.order)
        .map((section) => {
            const point = pointByVesselGradeId.get(section.vesselGradeId);
            return {
                vesselGradeId: section.vesselGradeId,
                name: section.name,
                weight: section.weight,
                type: section.type,
                uiGrade: point === null || point === undefined ? null : point * 10,
            };
        });
};

const Grading = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation("locale");
    const { inspectionId } = useParams();

    const { inspection, loading, error, savingGrades } = useAppSelector(selectInspection);
    const isDisabled = useAppSelector(selectInspectionIsDisabled);

    const [draft, setDraft] = useState<GradingSectionRow[]>([]);
    const [gradingLoading, setGradingLoading] = useState(true);
    const [saveDisabled, setSaveDisabled] = useState(true);
    // Guards the one-shot Crew Welfare auto-seed per inspection (A2-9093).
    const crewWelfareSeededRef = useRef<string | null>(null);

    // The inspection is loaded for the header and the read-only (status) gate.
    useEffect(() => {
        if (inspectionId) {
            dispatch(fetchInspectionData(inspectionId));
        }
    }, [inspectionId]);

    // Load the section template + recorded grades and build the draft. Resets
    // any unsaved edits. Failures surface as toasts from the service; an absent
    // template just yields an empty table.
    const loadGrading = useCallback(async () => {
        if (!inspectionId) {
            return;
        }

        setGradingLoading(true);
        try {
            const [sections, recorded] = await Promise.all([
                getGradingSections(inspectionId),
                getInspectionGrades(inspectionId),
            ]);
            setDraft(buildDraft(sections ?? [], recorded ?? []));
            setSaveDisabled(true);
        } finally {
            setGradingLoading(false);
        }
    }, [inspectionId]);

    useEffect(() => {
        loadGrading();
    }, [loadGrading]);

    // Seed the (blank) Crew Welfare row with the computed score and enable Save so
    // the reviewer can persist the suggested value.
    const seedCrewWelfareGrade = useCallback((score: number) => {
        setDraft((prev) =>
            prev.map((row) =>
                row.name === CREW_WELFARE_SECTION_NAME && row.uiGrade === null
                    ? { ...row, uiGrade: score }
                    : row
            )
        );
        setSaveDisabled(false);
    }, []);

    // A2-9093 (AC1): when the Grading page opens and the Crew Welfare section is
    // present but not yet graded, compute the automated grade (backend is the
    // single source of truth) and seed the editable box for the reviewer to
    // confirm. One-shot per inspection; only when editable (a full-report grade
    // is frozen) and only when the row is genuinely blank — so a value already
    // written at submit, or entered by a reviewer, is never overwritten.
    useEffect(() => {
        if (gradingLoading || !inspection || isDisabled || !inspectionId) {
            return;
        }
        if (crewWelfareSeededRef.current === inspectionId) {
            return;
        }

        crewWelfareSeededRef.current = inspectionId;

        const crewWelfareRow = draft.find((row) => row.name === CREW_WELFARE_SECTION_NAME);
        // Nothing to seed if the section is absent for this inspection type, or
        // it's already graded (e.g. written at submit / by a reviewer).
        if (crewWelfareRow?.uiGrade !== null) {
            return;
        }

        getCrewWelfareGrade(inspectionId).then((grade) => {
            if (grade) {
                seedCrewWelfareGrade(grade.scoreOutOf100);
            }
        });
    }, [gradingLoading, inspection, isDisabled, inspectionId, draft, seedCrewWelfareGrade]);

    // Recompute the summary grades on every edit — no save required. Ungraded
    // sections are excluded; a type with no graded sections stays blank.
    const summary = useMemo(
        () =>
            calculateSectionGrades(
                draft.map((row) => ({
                    type: row.type,
                    weight: row.weight,
                    point: row.uiGrade === null ? null : row.uiGrade / 10,
                }))
            ),
        [draft]
    );

    const onGradeChange = (vesselGradeId: number, value: number | null) => {
        setDraft((prev) =>
            prev.map((row) => (row.vesselGradeId === vesselGradeId ? { ...row, uiGrade: value } : row))
        );
        setSaveDisabled(false);
    };

    const onSave = async () => {
        if (!inspectionId) {
            return;
        }

        // The backend generates the condition comparison chart/summary on save
        // for allowed ship types — the frontend only sends the grades.
        const saved = await dispatch(updateInspectionSectionGrades(inspectionId, sectionGradesDto(draft)));

        if (saved) {
            setSaveDisabled(true);
        }
    };

    if (loading || gradingLoading) {
        return (
            <div className="flex h-full align-items-center justify-content-center">
                <ProgressSpinner />
            </div>
        );
    }

    if (error || !inspection) {
        return (
            <div className="flex align-items-center justify-content-center">
                <p>{t("grading.loadError")}</p>
            </div>
        );
    }

    return (
        <div>
            <div className="flex align-items-center justify-content-between mb-4 ml-2">
                <h2>{t("grading.title")}</h2>
            </div>
            <InspectionGradeInfo />
            <Card className="mt-4">
                <GradingSummary summary={summary} />
                <GradingSectionTable sections={draft} onGradeChange={onGradeChange} readOnly={isDisabled} />
                <div className="flex justify-content-end mt-3">
                    <Button
                        label={t("grading.saveChanges")}
                        disabled={saveDisabled || isDisabled || savingGrades}
                        loading={savingGrades}
                        onClick={onSave}
                    />
                </div>
            </Card>
        </div>
    );
};

export default Grading;
